import { useEffect, useState } from "react";
import { DataTable, Column } from "@/components/DataTable";
import { useExamApi } from "../api/examApi";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contaxt/AuthContext";

export interface ExamData {
  id: string;
  goal: string;
  cefr: string;
  date: string;
  examTypeLabel: string;
  action: string;
  examId: string;
  userId: string;
  feedback_json?: any;
}

export default function ExamHistoryPage() {
  const { getExamList } = useExamApi();
  const { isAuthenticated, getPaidUser } = useAuth();
  const [examData, setExamData] = useState<ExamData[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleView = (examId: string) => {
    const userId = getPaidUser()?.userId || "unknown_user";
    navigate(`/exam-details?examId=${examId}&userId=${userId}`);
  };

  const handleViewFeedback = (feedbackJson: any, examId: string) => {
    try {
      if (feedbackJson) {
        localStorage.setItem("combined_feedback_data", JSON.stringify(feedbackJson));
        localStorage.setItem("combined_feedback_exam_id", examId);
        navigate(`/comprehensive-feedback-results?from=list&examId=${examId}`, {
          state: { feedback: feedbackJson, examId },
        });
      }
    } catch (err) {
      console.error("Error storing feedback json:", err);
    }
  };

  // ⭐ PREMIUM HEADERS
  const examColumns: Column<ExamData>[] = [
    { key: "id", header: "Session" },
    {
      key: "goal",
      header: "Goal Level",
      render: (value) => (value ? value : "N/A"),
    },
    {
      key: "cefr",
      header: "Result (CEFR)",
      render: (value) => (value ? value : "N/A"),
    },
    {
      key: "date",
      header: "Completed On",
      render: (value) => (value ? value : "N/A"),
    },
    {
      key: "examTypeLabel",
      header: "Access Type",
      render: (value) => (
        <span className={value === "Free trial" ? "text-gray-600 font-medium" : "text-green-600 font-medium"}>
          {value}
        </span>
      ),
    },
    {
      key: "action",
      header: "Score Summary",
      render: (_value, row) => {
        const isGuest = row.examTypeLabel === "Free trial";
        if (isGuest) return <span className="text-muted-foreground">—</span>;

        return (
          <button className="text-blue-600 hover:underline" onClick={() => handleView(row.examId)}>
            View
          </button>
        );
      },
    },
    {
      key: "feedback",
      header: "Detailed Feedback",
      render: (_value, row) =>
        row.feedback_json ? (
          <button
            className="text-green-600 hover:underline"
            onClick={() => handleViewFeedback(row.feedback_json, row.examId)}
          >
            View Feedback
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;

      const paidUser = getPaidUser();
      if (!paidUser) return;

      try {
        const response = await getExamList();
        const list = response?.exams || [];

        const formattedData = list.map((item, index) => {
          const isGuest =
            item.id.startsWith("guest_") || item.session_id?.startsWith("guest_");

          return {
            id: (index + 1).toString(),
            goal: item.goal_level || "N/A",
            cefr: item.global_cefr || "N/A",
            date: item.created_at
              ? new Date(item.created_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "N/A",
            examTypeLabel: isGuest ? "Free trial" : "Paid",
            action: "View",
            examId: item.id,
            userId: paidUser.userId,
            feedback_json: item.feedback_json || null,
          };
        });

        setExamData(formattedData);
      } catch (err) {
        console.error("Failed to fetch exam history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, getPaidUser]);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground text-lg">Loading exam history...</p>
        </div>
      </div>
    );
  }

  if (examData.length === 0) {
    return <div className="text-center py-10 text-muted-foreground">No exam history found.</div>;
  }

  return (
    <DataTable
      title="Exam history"
      subtitle="Track your performance"
      data={examData}
      columns={examColumns}
    />
  );
}
